/**
 * useEffectiveReportTheme — single integration point for "theme
 * the report viewer should render with".
 *
 * Default behaviour: returns `useStore(s => s.reportTheme)`. Every
 * mount of `<SiteRagReportView>` reads through this hook so the
 * shipped global theme flows naturally.
 *
 * Override behaviour: when an ancestor wraps in
 * `<ThemeOverrideProvider value={localTheme}>`, the hook returns
 * `localTheme` instead. This is how the SLK-S Template Studio
 * preview pane shows the user's draft theme without mutating the
 * global Zustand store (mutating would re-render every other
 * surface in the app).
 *
 * Consumed by:
 *   - SiteRagReportView (one read at line 402; SLK-H swaps the
 *     `reportTheme: theme` destructure for this hook)
 *   - SleekSection / SleekKPI / SleekTable / SleekAlert / SleekTOC /
 *     SleekDarkInfoCard / SleekChartTooltip (SLK-H primitives use
 *     this hook directly so the override flows through)
 *   - DOCX export: NOT applicable — DOCX runs server-side without
 *     React context; SLK-D consumes theme as a function argument.
 */
import React from 'react';
import { useStore } from '../store';
import { DEFAULT_THEME, type ReportTheme } from '../types/reportTheme';
import { attachReaderMode } from './reportRoleColors';
import { ReportReaderModeContext } from './useReportReaderMode';

const ThemeOverrideContext = React.createContext<ReportTheme | null>(null);

/** Defensive brand coercion. Reports generated against a stored template/theme
 *  saved BEFORE the Sitemark→Datacrate rebrand still carry pre-rebrand brand
 *  strings ("Deep Atomic", deepatomic.com) in footer/header/contact fields —
 *  which then render in the running header + end-matter footer. Coerce every
 *  string field of the resolved theme to the current brand so no stale name
 *  leaks into a rendered report (app view AND the Chromium PDF use this hook). */
function coerceBrandStrings<T>(value: T): T {
  if (typeof value === 'string') {
    return value
      .replace(/DEEP ATOMIC/g, 'DATACRATE')
      .replace(/Deep Atomic/g, 'Datacrate')
      .replace(/deepatomic\.(com|io)/g, 'datacrate.io') as unknown as T;
  }
  if (Array.isArray(value)) return value.map(coerceBrandStrings) as unknown as T;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = coerceBrandStrings(v);
    return out as unknown as T;
  }
  return value;
}

/**
 * Wrap a subtree in this Provider to make every
 * `useEffectiveReportTheme()` call inside it return the supplied
 * theme instead of the store's theme. Used by SLK-S Template Studio's
 * `<TemplatePreviewPane>` to render the viewer with the user's draft
 * theme without polluting the global store.
 */
export function ThemeOverrideProvider({
  value,
  children,
}: {
  value: ReportTheme;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <ThemeOverrideContext.Provider value={value}>
      {children}
    </ThemeOverrideContext.Provider>
  );
}

/**
 * Resolve the theme to render with: override wins if present,
 * else falls through to the store, else falls through to
 * DEFAULT_THEME. Pure synchronous; safe to call inside render.
 */
export function useEffectiveReportTheme(): ReportTheme {
  const override = React.useContext(ThemeOverrideContext);
  // Reader color mode (light/dark) provided at the report root. Default
  // 'light' outside a report / in export hosts that never provide it, so the
  // stamp is a no-op there and the light baseline is byte-identical. Stamped
  // via a Symbol key (invisible to JSON.stringify) so it can never persist.
  const readerMode = React.useContext(ReportReaderModeContext);
  const storeTheme = useStore(s => s.reportTheme);
  const resolved = override ?? storeTheme ?? DEFAULT_THEME;
  return React.useMemo(
    () => attachReaderMode(coerceBrandStrings(resolved), readerMode),
    [resolved, readerMode],
  );
}
